import React, { ButtonHTMLAttributes, FC, HTMLAttributes, ReactNode } from "react";

import classnames from "classnames";
import { twMerge } from "tailwind-merge";

export interface NavBarProps extends HTMLAttributes<HTMLElement> {
  ariaLabel?: string;
}

export const NavBar: FC<NavBarProps> = ({ children, className, ariaLabel = "Primary navigation", ...props }) => (
  <nav className={twMerge("si-navbar bg-base-100 shadow-sm", className)} aria-label={ariaLabel} {...props}>
    {children}
  </nav>
);

export const NavBarStart: FC<HTMLAttributes<HTMLDivElement>> = ({ children, className, ...props }) => (
  <div className={twMerge("si-navbar-start", className)} {...props}>
    {children}
  </div>
);

export const NavBarCenter: FC<HTMLAttributes<HTMLDivElement>> = ({ children, className, ...props }) => (
  <div className={twMerge("si-navbar-center", className)} {...props}>
    {children}
  </div>
);

export const NavBarEnd: FC<HTMLAttributes<HTMLDivElement>> = ({ children, className, ...props }) => (
  <div className={twMerge("si-navbar-end", className)} {...props}>
    {children}
  </div>
);

export interface NavBarMenuProps extends HTMLAttributes<HTMLUListElement> {
  horizontal?: boolean;
}

export const NavBarMenu: FC<NavBarMenuProps> = ({ children, className, horizontal = true, ...props }) => (
  <ul className={twMerge(classnames("si-menu", "m-0", { "si-menu-horizontal": horizontal }), className)} {...props}>
    {children}
  </ul>
);

export interface NavBarMenuItemProps extends HTMLAttributes<HTMLElement> {
  href?: string;
  active?: boolean;
  disabled?: boolean;
  target?: string;
  rel?: string;
}

export const NavBarMenuItem: FC<NavBarMenuItemProps> = ({ children, className, active, disabled, href, onClick, target, rel, ...props }) => {
  const itemClassName = twMerge(
    classnames({
      "si-menu-active": active,
      "si-menu-disabled pointer-events-none": disabled,
    }),
    className,
  );

  return (
    <li>
      {href && !disabled ? (
        <a href={href} className={itemClassName} aria-current={active ? "page" : undefined} onClick={onClick} target={target} rel={rel} {...props}>
          {children}
        </a>
      ) : (
        <button type="button" className={itemClassName} disabled={disabled} aria-current={active ? "page" : undefined} onClick={onClick} {...props}>
          {children}
        </button>
      )}
    </li>
  );
};

export interface NavBarDropdownProps extends HTMLAttributes<HTMLLIElement> {
  label: ReactNode;
  menuClassName?: string;
}

export const NavBarDropdown: FC<NavBarDropdownProps> = ({ children, className, label, menuClassName, ...props }) => (
  <li className={className} {...props}>
    <details>
      <summary>{label}</summary>
      <ul className={twMerge("z-dropdown rounded-box bg-base-100 p-2 shadow-sm", menuClassName)}>{children}</ul>
    </details>
  </li>
);

export interface NavBarDropdownMenuProps extends HTMLAttributes<HTMLDivElement> {
  trigger: ReactNode;
  triggerClassName?: string;
  menuClassName?: string;
  align?: "start" | "end";
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

export const NavBarDropdownMenu: FC<NavBarDropdownMenuProps> = ({
  children,
  className,
  trigger,
  triggerClassName,
  menuClassName,
  align = "start",
  triggerProps,
  ...props
}) => (
  <div className={twMerge(classnames("si-dropdown", { "si-dropdown-end": align === "end" }), className)} {...props}>
    <button type="button" tabIndex={0} className={twMerge("si-btn si-btn-ghost", triggerClassName)} {...triggerProps}>
      {trigger}
    </button>
    <ul tabIndex={-1} className={twMerge("si-menu si-dropdown-content z-dropdown mt-3 w-56 rounded-box bg-base-100 p-2 shadow", menuClassName)}>
      {children}
    </ul>
  </div>
);

export default NavBar;
